import { useState, useEffect } from "react";
import { createUseStyles } from "react-jss";
import { Grid, GridColumn, Heading, Skeleton, useSnackbar } from "@livingdesign/react";
import { useSelector } from "react-redux";
import { getService } from "../../../axios/axios";
import { ORGANIZATION_SERVICE } from "../../../constants/baseURLs";
import { ADDRESS_LINE_1, ADDRESS_LINE_2, CITY, COUNTRY, NO, STATE, YES, ZIPCODE } from "../../../constants/constants";
import { cleanValueString, getErrorMessage, renderTernary } from "../../../helpers/utils";
import { amountFormat, formatPhoneNumber } from "../../../helpers/commonUtils";
import { Typography } from "@mui/material";
import MuiDataTable from "../../../components/Table/MuiDataTable/MuiDataTable";

const useStyles = createUseStyles({
	container: {
		paddingBottom: "24px"
	},
	contactContainer: {
		backgroundColor: "#ffffff",
		marginBottom: "32px",
		padding: "24px"
	},
	spacing24: {
		paddingTop: 20,
	},
	spacing36: {
		paddingBottom: 10,
	},
	pcGridLeftInside: {
		display: "inline-flex",
		width: "100%",
	},
	pcTitle: {
		fontSize: "14px !important",
		fontFamily: "Bogle !important",
		fontWeight: 'bold !important'
	},
	rightContactContent: {
		display: 'flex',
		flexWrap: 'wrap'
	},
	pcTitleAnswer: {
		fontSize: 14,
		fontFamily: "Bogle",
	},
	fontSize14: {
		fontSize: "14px !important",
		fontFamily: "Bogle !important",
	},
})

const OrganizationDetails = (props) => {
	const classes = useStyles();
	const { addSnack } = useSnackbar();
	const { loading } = props;
	const [totalBudgetAmount, setTotalBudgetAmount] = useState({ balanceAmount: 0, budgetAmount: 0 });
	const {
		identifier,
		addressType,
		addressLine1,
		addressLine2,
		city,
		state,
		country,
		email,
		phoneNo,
		faxNo,
		mELApproved,
		expertise,
		primaryExpertise,
		taxId,
		billingContactName,
		relationshipPartnerName,
		relationshipPartnerEmail,
		zipCode,
		firmSize,
		internalContactName,
		internalContactEmail,
		officeLocationCity,
		officeLocationState,
		officeLocationCountry,
		statePracticesCountry,
		statePracticesState,
		statePracticesRegion,
		officeLocations,
		statePractices
	} = useSelector((state) => state?.organization?.organizationDetailValues);
	const [officeLocationsData, setOfficeLocationsData] = useState([]);
	const [statePracticesData, setStatePracticesData] = useState([]);

	const getTotalBudgetAmount = (data) => {
		let budgetAmount = 0;
		let balanceAmount = 0;
		data?.map((a) => {
			balanceAmount = balanceAmount + (a?.balanceAmount ?? 0);
			budgetAmount = budgetAmount + (a?.budgetAmount ?? 0);
		})
		setTotalBudgetAmount({ balanceAmount, budgetAmount })
	}

	useEffect(() => {
		setOfficeLocations();
		setStatePractices();
	}, [])

	const setOfficeLocations = () => {
		if (officeLocations) {
			try {
				const locations = JSON.parse(officeLocations).map((location, index) => ({
					id: index,
					country: location.country || '',
					state: location.state || '',
					city: location.city || '',
					countryCode: location.countryCode || 346
				}));
				setOfficeLocationsData(locations);
			} catch (error) {
				setOfficeLocationsData([]);
			}
		} else if (officeLocationCountry) {
			setOfficeLocationsData([{
				id: 0,
				country: officeLocationCountry,
				state: officeLocationState,
				city: officeLocationCity,
				countryCode: 346
			}]);
		} else {
			setOfficeLocationsData([]);
		}
	};

	const setStatePractices = () => {
		if (statePractices) {
			try {
				const practices = JSON.parse(statePractices).map((practice, index) => ({
					id: index,
					country: practice.country || '',
					state: practice.state || '',
					region: Array.isArray(practice.region)
						? practice.region
						: (practice.region ? practice.region.split(",") : []),
					countryCode: practice.countryCode || 346
				}));
				setStatePracticesData(practices);
			} catch (error) {
				setStatePracticesData([]);
			}
		} else if (statePracticesCountry) {
			setStatePracticesData([{
				id: 0,
				country: statePracticesCountry,
				state: statePracticesState,
				region: Array.isArray(statePracticesRegion)
					? statePracticesRegion
					: (statePracticesRegion ? statePracticesRegion.split(",") : []),
				countryCode: 346
			}]);
		} else {
			setStatePracticesData([]);
		}
	}

	const getBudgetOrgData = () => {
		getService(
			ORGANIZATION_SERVICE,
			`/budgets/v1/organizations/${identifier}`
		).then((response) => {
			const data = response?.data?.budgets;
			getTotalBudgetAmount(data);
		}).catch((error) => {
			setTotalBudgetAmount({ balanceAmount: 0, budgetAmount: 0 })
			addSnack({
				message: getErrorMessage(error)
			});
		});
	}

	const renderOfficeLocations = () => {
		let displayData = officeLocationsData;

		// Parse if officeLocations is a string
		if (typeof officeLocationsData === 'string') {
			try {
				displayData = JSON.parse(officeLocationsData);
			} catch (error) {
				console.warn('Failed to parse officeLocations:', error);
			}
		}

		// Define columns for MuiDataTable
		const officeLocationColumns = [
			{
				name: "country",
				label: "Country",
				options: {
					filter: false,
					sort: false,
					searchable: false,
					customBodyRender: (value, tableMeta) => {
						const location = officeLocationsData[tableMeta.rowIndex];
						return location.country;
					},
					setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold' } })
				}
			},
			{
				name: "state",
				label: "State",
				options: {
					filter: false,
					sort: false,
					searchable: false,
					customBodyRender: (value, tableMeta) => {
						const location = officeLocationsData[tableMeta.rowIndex];
						return location.state;
					},
					setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold' } })
				}
			},
			{
				name: "city",
				label: "City",
				options: {
					filter: false,
					sort: false,
					searchable: false,
					customBodyRender: (value, tableMeta) => {
						const location = officeLocationsData[tableMeta.rowIndex];
						return location.city ;
					},
					setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold' } })
				}
			},
		];

		const officeLocationTableOptions = {
			rowsPerPage: 15,
			rowsPerPageOptions: [15, 25, 50],
			selectableRows: 'none',
			responsive: 'standard',
			elevation: 0,
		};

		return (
			<>
				<MuiDataTable
					data={displayData}
					columns={officeLocationColumns}
					disableSearch={true}
					disableFilter={true}
					disableExport={true}
					hideAddNewButton={true}
					customToolbar={<span className={classes.pcTitle}>Office Locations:</span>}
					customOptions={officeLocationTableOptions}
					noResultsText="No office locations added"
				/>
			</>
		);
	}

	const renderStatePractices = () => {
		let displayData = statePracticesData;

		// Define columns for MuiDataTable
		const statePracticesColumns = [
			{
				name: "country",
				label: "Country",
				options: {
					filter: false,
					sort: false,
					searchable: false,
					customBodyRender: (value, tableMeta) => {
						const location = displayData[tableMeta.rowIndex];
						return location.country;
					},
					setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold'} }),
				}
			},
			{
				name: "state",
				label: "State",
				options: {
					filter: false,
					sort: false,
					searchable: false,
					customBodyRender: (value, tableMeta) => {
						const location = displayData[tableMeta.rowIndex];
						return location.state;
					},
					setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold'}})
				}
			},
			{
				name: "region",
				label: "Region",
				options: {
					filter: false,
					sort: false,
					searchable: false,
					customBodyRender: (value, tableMeta) => {
						const location = displayData[tableMeta.rowIndex];
						return location.region?.join(', ');
					},
					setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold' } })
				}
			}
		];

		const statePracticesTableOptions = {
			rowsPerPage: 15,
			rowsPerPageOptions: [5, 10, 15, 25, 50],
			selectableRows: 'none',
			responsive: 'standard',
		};

		return (
			<MuiDataTable
				data={displayData}
				columns={statePracticesColumns}
				customToolbar={<span className={classes.pcTitle}>State Practices:</span>}
				disableSearch={true}
				disableFilter={true}
				disableExport={true}
				hideAddNewButton={true}
				customOptions={statePracticesTableOptions}
				noResultsText="Click 'Add New' to add State Practices"
			/>
		);


	}

	useEffect(() => {
		getBudgetOrgData();
	}, [])

	return <div className={classes.container} data-testid="org-detail-tab-content">
		<div className={classes.contactContainer}>
			<Grid UNSAFE_className={classes.spacing36}>
				<Heading as="h3" size="small" id="contact">
					Details
				</Heading>
			</Grid>
			<Grid>
				<GridColumn sm={6} md={6} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>Firm Size:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(firmSize)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={6} md={6} lg={6}></GridColumn>
				<GridColumn sm={6} md={6} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>Expertise:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(expertise)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>Primary Expertise:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : (cleanValueString(primaryExpertise) ?? "-")}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
			</Grid>
		</div>
		<div className={classes.contactContainer}>
			<Grid UNSAFE_className={classes.spacing36}>
				<Heading as="h3" size="small" id="contact">
					Contact
				</Heading>
			</Grid>
			<Grid>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>Phone Number:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : (formatPhoneNumber(phoneNo) ?? "-")}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>Fax Number:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(faxNo)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>Address Type:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={6}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(addressType)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>{ADDRESS_LINE_1}</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={6}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(addressLine1)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>{ADDRESS_LINE_2}</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(addressLine2)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>{CITY}</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(city)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>{STATE}</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(state)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>{COUNTRY}</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(country)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>{ZIPCODE}</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(zipCode)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={3}>
								<Typography className={classes.pcTitle}>Relationship Partner Name:</Typography>
							</GridColumn>
							<GridColumn lg={1}>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(relationshipPartnerName)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={3}>
								<Typography className={classes.pcTitle}>Relationship Partner Email:</Typography>
							</GridColumn>
							<GridColumn lg={1}>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(relationshipPartnerEmail)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={3}>
								<Typography className={classes.pcTitle}>Internal Contact Name:</Typography>
							</GridColumn>
							<GridColumn lg={1}>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(internalContactName)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={3}>
								<Typography className={classes.pcTitle}>Internal Contact Email:</Typography>
							</GridColumn>
							<GridColumn lg={1}>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(internalContactEmail)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12}>
					{renderOfficeLocations()}
				</GridColumn>
			</Grid>
		</div>
		<div className={classes.contactContainer}>
			<Heading as="h3" size="small" id="authorization">
				Authorization
			</Heading>
			<Grid>
				<GridColumn sm={6} md={6} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>MEL Approved:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={6}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : (renderTernary(mELApproved, YES, NO))}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12}>
					{renderStatePractices()}
				</GridColumn>
			</Grid>
		</div>
		<div className={classes.contactContainer}>
			<Heading as="h3" size="small" id="financial">
				Financial
			</Heading>
			<Grid>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>Billing Contact Name:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={8}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(billingContactName)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>Total Budgeted:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={6}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" /> : `$ ${cleanValueString(amountFormat(totalBudgetAmount?.budgetAmount))}`}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
			</Grid>
			<Grid>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={4}>
								<Typography className={classes.pcTitle}>Total Spent:</Typography>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={6}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14}>{loading ? <Skeleton width="150px" height="15px" />
										: `$ ${amountFormat((totalBudgetAmount?.budgetAmount ?? 0) - (totalBudgetAmount?.balanceAmount ?? 0))}`}
									</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
			</Grid>
			<Grid>
				<GridColumn sm={12} md={12} lg={6}>
					<Grid UNSAFE_className={classes.spacing24}>
						<Grid UNSAFE_className={classes.pcGridLeftInside}>
							<GridColumn sm={2} lg={3}>
								<Typography className={classes.pcTitle}>Tax ID:</Typography>
							</GridColumn>
							<GridColumn lg={1}>
							</GridColumn>
							<GridColumn UNSAFE_className={classes.rightContactContent} sm={6} lg={6}>
								<Grid UNSAFE_className={classes.pcTitleAnswer}>
									<Typography className={classes.fontSize14} id="tax-id">{loading ? <Skeleton width="150px" height="15px" /> : cleanValueString(taxId)}</Typography>
								</Grid>
							</GridColumn>
						</Grid>
					</Grid>
				</GridColumn>
			</Grid>
			<Grid>
			</Grid>
		</div>
	</div>
}
export default OrganizationDetails
